import { ITimeParts, DateTimeHelper } from 'src/app/core/helper/datetime.helper';
import { Bid } from './bid';
import { Asset } from 'src/app/data/schema/asset';

export enum ItemType {
    BidItem = 1,
    BuyItNow = 2,
    Donation = 3,
    BlindBid = 4,
    LiveItem = 5,
}

export class AuctionItem {
    auctionId: number;
    bids: Bid[] = [];
    category: string;
    categoryId: number;
    currentBid: number;
    currentBidStr: string;
    currentWinner: string;
    currentWinnerEmail: string;
    currentWinnerPhone: string;
    currentWinnerFirstName: string;
    currentWinnerLastName: string;
    deleted: boolean;
    identifier: string;
    images: Asset[] = [];
    minimumIncrement = 1.0;
    newImages: string[] = [];
    productId: number;
    reserveAmount = 0.0;
    startingBid = 1.0;
    status = 0;
    winningUserId: number;
    code: string;
    type: ItemType = ItemType.BidItem;
    quantity: number;
    approximateValue: number;
    buyNowCount = 0;
    donorImage: string;
    newDonorImage: string;
    donor: string;
    notes: string;
    videoUrl: string;
    infoUrl: string;
    maxBid: number; // used for the auto-bid
    sortOrder: number;
    hideUntilOpen: boolean = false;
    extensionCount: number = 0;
    extensionDuration: number = 0;
    lastBidDisplay: string;
    featured: boolean;
    bidCount: number = 0;

    // for shipping, liquor taxes, taxes
    fee1: number = 0;
    fee2: number = 0;
    tax1Applies: boolean = false;
    tax2Applies: boolean = false;

    // recently added when vouchers were
    createdByName: string;
    createdOn: string
    createdOnDisplay: string;

    // for vouchers
    voucherId: number;
    voucherCost: number;
    voucherPmtReady: boolean;
    voucherPaid: boolean;
    voucherSent: boolean;
    voucherLimit: number;
    voucherImgPaths: string;
    voucherVidUrl: string;
    voucherInfoUrl: string;
    stripeDesc: string
    closeVerified: boolean = false;
    voucherImagePathArray: string[] = []

    // cbs fields
    title: string;
    openAt: string;
    closeAt: string;
    body: string;
    surveyId: number;

    private endFmt: string;
    private startFmt: string;
    private hidden: boolean = false;
    private lastBidDate = new Date(0);
    private minBid: number;

    private didOpen: boolean = false;
    private didClose: boolean = false;


    private remaining: string;
    private remainingObj: ITimeParts;


    public constructor(init?: Partial<AuctionItem>) {
        Object.assign(this, init);

        // Default always to starting bid
        this.minBid = this.startingBid;

        if (this.bids.length > 0) {
            // make sure the bids are turned into valid values
            this.bids = this.bids.map(m => new Bid(m));
            this.getLastBidDate();
            // Get the minimum bid (only applies to biddable)
            if (this.type == ItemType.BidItem) {
                this.minBid = this.currentBid;
                this.minBid += this.minimumIncrement;
            }
        }

        // If the minimum is > max then 0 out the max
        if (this.maxBid < this.minBid) {
            this.maxBid = 0;
        }

        if (this.voucherId > 0) {
            this.videoUrl = this.voucherVidUrl;
            this.infoUrl = this.voucherInfoUrl;
            this.voucherImagePathArray = [];
            if (this.voucherImgPaths != null) {
                const pathArray = this.voucherImgPaths.split(',');
                for (let i = 0; i < pathArray.length; i++) {
                    if (pathArray[i].trim().length > 0) {
                        this.voucherImagePathArray.push(pathArray[i]);
                    }
                }
                this.voucherImgPaths = this.voucherImagePathArray.join(',');
            }
        }

        // trigger an update to ensure everything is set
        this.updateDates();
        this.update();
        // set close verified to the same as has closed so we know the constructor has run on the item while it was closed
        this.closeVerified = this.hasClosed;
    }

    get allowBidEntry() {
        return (
            this.type === ItemType.BidItem ||
            this.type === ItemType.BlindBid ||
            this.type === ItemType.Donation
        );
    }

    get isBidOn() {
        return (
            this.type === ItemType.BidItem ||
            this.type === ItemType.BlindBid
        );
    }

    get hasOpened() { return this.didOpen; }
    get hasClosed() { return this.didClose; }
    get isOpen() { return this.hasOpened && !this.hasClosed; }

    get showQuickBid() { return (this.type === ItemType.BidItem); }
    get isBuyNow() { return (this.type === ItemType.BuyItNow); }
    get isDonation() { return (this.type === ItemType.Donation); }
    get showAllBids() { return (this.type === ItemType.BidItem); }
    get canBeBought() { return (this.isBuyNow && this.quantity > 0); }

    get lastBid() { return this.lastBidDate; }
    get paidFor() { return this.getStatusFlag(1); }
    set paidFor(value: boolean) { this.setStatusFlag(value, 1); }

    get pickedUp() { return this.getStatusFlag(2); }
    set pickedUp(value: boolean) { this.setStatusFlag(value, 2); }

    get minimumBid() { return this.minBid; }

    get reserveMet() {
        if (this.type == ItemType.BlindBid && this.closeVerified && this.winningUserId > 0) {
            // get the max bid for the winning userId
            const max = this.bids.filter(m => m.userId === this.winningUserId).reduce((prev, current) => (prev.value > current.value) ? prev : current);
            return max.value >= this.reserveAmount;
        } else {
            return this.currentBid >= this.reserveAmount;
        }
    }

    get startDisplay() { return this.startFmt; }
    get endDisplay() { return this.endFmt; }

    get hidden4Guests() { return this.hidden; }
    set hidden4Guests(val) { this.hidden = val; }

    get timeRemaining() { return this.remaining; }
    get timeRemainingObj() { return this.remainingObj; }


    getBidById(bidId: number) {
        return this.bids.find(m => m.bidId === bidId);
    }

    setWinningBid(bid: Bid) {
        if (this.hasClosed === false) {
            this.winningUserId = bid.userId;
            this.currentWinner = bid.displayName;
            this.currentBid = +bid.value;
            this.currentBidStr = bid.amount;
        } else {
            console.log('Auction has already closed');
        }
    }

    getTotalValue() {
        // Donation items value is based on the sum of all bids
        if (this.type === ItemType.Donation) {
            // 0 bids = no value
            if (this.bids.length === 0) {
                return 0;
            }
            // First lets get the total amount of all bids no null (should never happen);
            const bidTotals = this.bids.map(m => m.amount != null ? +m.amount.substring(1) : 0);
            let bidTotal = 0;
            if (bidTotals.length > 0) {
                bidTotal = bidTotals.reduce((prev, next) => prev + next);
            }
            return bidTotal;
        }
        return this.currentBid;
    }

    removeBid(bid: Bid) {
        bid = new Bid(bid);
        // First lets locate the bid, then remove it
        const id = this.bids.findIndex(m => (m.bidId === bid.bidId));
        if (id === -1) {
            console.log('Bid not found');
            return false;
        }

        this.bids.splice(id, 1);

        // Now, grab the last bid in the list and update the product info to be the current bid
        if (this.bids.length === 0) {
            this.winningUserId = null;
            this.currentWinner = '';
            this.currentBid = +this.startingBid;
            this.currentBidStr = `${this.currentBidStr.substring(0, 1)}${this.startingBid}`;
        } else if (id === 0) {
            this.setWinningBid(this.bids[0]);
        }

        // If this was a buy it now, then lets add the quantity back
        if (this.isBuyNow) {
            this.quantity++;
        }

        // Get the minimum bid (required for regular bid items)
        if (this.type == ItemType.BidItem) {
            this.minBid = this.currentBid;
            if (this.type === ItemType.BidItem && this.bids.length > 0) {
                this.minBid += this.minimumIncrement;
            }
        } else {
            this.minBid = this.startingBid;
        }

        this.getLastBidDate();

        return true;
    }

    addBid(bid: Bid, myBid: boolean = false) {

        bid = new Bid(bid);
        this.bids.push(bid);
        this.bids.sort((a, b) => this.sortBids(a, b));

        const bidAmount = +bid.value;
        if (this.currentBid <= bidAmount && (this.type != ItemType.BlindBid || myBid)) {
            // new bid is higher, so update the products info
            this.setWinningBid(bid);
        }

        // Get the minimum bid (required for regular bid items)
        if (this.type == ItemType.BidItem) {
            this.minBid = this.currentBid;
            if (this.type === ItemType.BidItem && this.bids.length > 0) {
                this.minBid += this.minimumIncrement;
            }
        } else {
            this.minBid = this.startingBid;
        }

        // If this was a buy it now, then lets take away from the qty
        if (this.isBuyNow) {
            this.quantity--;
        }

        // If the bid value > max, then 0 out the max
        if (bid.value > this.maxBid) {
            this.maxBid = 0;
        }

        this.getLastBidDate();
    }

    update() {
        // determine if the item has started or ended  
        const now = new Date().valueOf();
        this.didOpen = (new Date(this.openAt).valueOf() <= now);
        this.didClose = (new Date(this.closeAt).valueOf() <= now);

        // Update the time remaining for the item
        if (this.didClose === true) {
            this.remaining = 'Item has closed!';
        } else if (this.hasOpened === false) {
            const remaining = DateTimeHelper.getTimeRemaining(undefined, new Date(this.openAt).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${remaining.clock}`;
        } else {
            const remaining = DateTimeHelper.getTimeRemaining(new Date(this.openAt).valueOf(), new Date(this.closeAt).valueOf());
            this.remainingObj = remaining.parts;
            this.remaining = `${(this.extensionDuration > 0 ? '~' : '')}${remaining.clock}`;
        }
    }

    updateDates() {
        // Ensure proper format of date
        if (this.openAt && !this.openAt.endsWith('Z')) {
            this.openAt = this.openAt + 'Z';
        }
        if (this.closeAt && !this.closeAt.endsWith('Z')) {
            this.closeAt = this.closeAt + 'Z';
        }
        // format the dates for display
        this.startFmt = DateTimeHelper.formatDate(this.openAt);
        this.endFmt = DateTimeHelper.formatDate(this.closeAt);
        if (this.createdOn != null) {
            this.createdOnDisplay = DateTimeHelper.formatDate(this.createdOnDisplay);
        }
    }


    private getLastBidDate() {
        if (this.bids.length > 0) {
            const sortedBids = this.bids.sort((a, b) => b.value - a.value);
            this.lastBidDisplay = sortedBids[0].placedString;
            this.lastBidDate = new Date(sortedBids[0].placed);
        } else {
            this.lastBidDisplay = DateTimeHelper.formatDate(new Date(0));
        }
    }

    private sortBids(a, b) {
        return (+b.value - +a.value);
    }

    private getStatusFlag(value: number) {
        // tslint:disable-next-line:no-bitwise
        return ((this.status & value) === value);
    }

    private setStatusFlag(enabled: boolean, value: number) {
        if (enabled === true) {
            // tslint:disable-next-line:no-bitwise
            this.status |= value;
            // tslint:disable-next-line:no-bitwise
        } else if ((this.status & value) === value) {
            this.status -= value;
        }
    }

}
